import { useState } from 'react';
import { Link2, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog.jsx';
import { ApiError } from '@/lib/api.js';
import { getAddPilotLink, resetAddPilotLink } from '@/services/deals.service.js';

// The client add-pilot link (David 2026-09-22). One reusable link per policy:
// the client opens it already verified and adds as many pilots as they need,
// so there is no "this link was already used" dead end. Reset is the handle
// for a link that went somewhere it should not have.
const errMsg = (err) => (err instanceof ApiError ? (err.body?.error || err.message) : err?.message || 'unknown error');

export default function AddPilotLinkButton({ dealId }) {
  const [busy, setBusy] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  async function copy(res, verb) {
    try {
      await navigator.clipboard.writeText(res.link);
      toast.success(`Add-pilot link ${verb}. It works for every pilot on this policy until you reset it.`);
    } catch {
      toast.info(`Add-pilot link ${verb}: ${res.link}`);
    }
    if (res.has_primary === false) {
      toast.info('This deal has no primary contact, so adds through the link are not attributed to anyone.');
    }
  }

  async function onCopy() {
    setBusy(true);
    try {
      const res = await getAddPilotLink(dealId);
      await copy(res, 'copied');
    } catch (err) {
      toast.error(`Add-pilot link failed: ${errMsg(err)}`);
    } finally {
      setBusy(false);
    }
  }

  async function onReset() {
    setBusy(true);
    try {
      const res = await resetAddPilotLink(dealId);
      await copy(res, 'reset and the new one copied');
      setConfirmReset(false);
    } catch (err) {
      toast.error(`Reset failed: ${errMsg(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center">
      <Button
        type="button" size="sm" variant="ghost" disabled={busy} onClick={onCopy}
        title="Copy a link the client can use to add pilots to this policy, no email or tail lookup"
        className="h-7 gap-1.5 px-2 text-text-muted hover:text-accent"
      >
        <Link2 size={13} strokeWidth={1.5} />
        Client add-pilot link
      </Button>
      <Button
        type="button" size="sm" variant="ghost" disabled={busy} onClick={() => setConfirmReset(true)}
        title="Reset the client add-pilot link"
        aria-label="Reset the client add-pilot link"
        className="h-7 px-1.5 text-text-dim hover:text-accent"
      >
        <RotateCcw size={12} strokeWidth={1.5} />
      </Button>
      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset the add-pilot link?</AlertDialogTitle>
            <AlertDialogDescription>
              The link you sent this client stops working and a new one is copied. Anyone opening the old link is
              asked for their email and tail number instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Back</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); onReset(); }} disabled={busy}>
              {busy ? 'Resetting...' : 'Reset and copy'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
